import { EGenderDTO, IPatientToInvite } from '@project/view-models';
import { DateFormatter, DEFAULT_DATE_FORMAT, LibValidators, TGuid } from '@core/helpers';
import { TranslateService } from '@project/translate';
import { SharedFormOptions } from '@project/shared';

export interface IPatientInviteFormModel {
  cpf?: string;
  email?: string;
  gender: EGenderDTO;
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  phone?: string;
  tag?: TGuid;
  cns?: string;
  externalId?: string;
  isAtypicalChildGuardian?: boolean | null;
}

export class PatientInviteUtils {
  static getPatientFormDeclaration(customSkippedFields?: string[], customRequiredFields?: string[]) {
    let fieldValidation: Record<string, any> = {
      email: ['', [LibValidators.email(TranslateService.localize('validations.invalid-email'))]],
      cpf: ['', [LibValidators.cpf(TranslateService.localize('validations.invalid'))]],
      externalId: ['', []],
      gender: [
        SharedFormOptions.genderOptions()[0].value,
        [
          LibValidators.required(TranslateService.localize('validations.required')),
          LibValidators.enumValue(EGenderDTO, TranslateService.localize('validations.not-allowed')),
        ],
      ],
      firstName: ['', [LibValidators.required(TranslateService.localize('validations.required'))]],
      lastName: ['', [LibValidators.required(TranslateService.localize('validations.required'))]],
      dateOfBirth: [
        '',
        [
          LibValidators.date(DEFAULT_DATE_FORMAT, TranslateService.localize('validations.invalid')),
          LibValidators.required(TranslateService.localize('validations.required')),
        ],
      ],
      phone: [
        '',
        [
          LibValidators.required(TranslateService.localize('validations.required')),

          LibValidators.cellphone(TranslateService.localize('validations.phone-invalid')),
        ],
      ],
      tag: [],
      cns: ['', [LibValidators.cns(TranslateService.localize('validations.invalid'))]],
      isAtypicalChildGuardian: [null],
    };

    fieldValidation = PatientInviteUtils.applySkippedFields(fieldValidation, customSkippedFields);
    fieldValidation = PatientInviteUtils.applyRequiredFields(fieldValidation, customRequiredFields);

    return fieldValidation;
  }

  static getPatientInviteDependentFormDeclaration(customSkippedFields?: string[], customRequiredFields?: string[]) {
    let fieldValidation: Record<string, any> = {
      email: [{ value: '', disabled: true }, []],
      cpf: ['', [LibValidators.cpf(TranslateService.localize('validations.invalid'))]],
      gender: [
        SharedFormOptions.genderOptions()[0].value,
        [
          LibValidators.required(TranslateService.localize('validations.required')),
          LibValidators.enumValue(EGenderDTO, TranslateService.localize('validations.not-allowed')),
        ],
      ],
      firstName: ['', [LibValidators.required(TranslateService.localize('validations.required'))]],
      lastName: ['', [LibValidators.required(TranslateService.localize('validations.required'))]],
      dateOfBirth: [
        '',
        [
          LibValidators.date(DEFAULT_DATE_FORMAT, TranslateService.localize('validations.invalid')),
          LibValidators.required(TranslateService.localize('validations.required')),
        ],
      ],
      phone: [
        '',
        [
          LibValidators.required(TranslateService.localize('validations.required')),
          LibValidators.cellphone(TranslateService.localize('validations.phone-invalid')),
        ],
      ],
      tag: [],
      cns: [
        '',
        [
          LibValidators.cns(TranslateService.localize('validations.invalid')),
          LibValidators.required(TranslateService.localize('validations.required')),
        ],
      ],
    };

    fieldValidation = PatientInviteUtils.applySkippedFields(fieldValidation, customSkippedFields);
    fieldValidation = PatientInviteUtils.applyRequiredFields(fieldValidation, customRequiredFields);

    return fieldValidation;
  }

  static getPatientImportFormDeclaration(customSkippedFields?: string[], customRequiredFields?: string[]) {
    let fieldValidation: Record<string, any> = {
      email: [
        '',
        [
          LibValidators.email(TranslateService.localize('validations.invalid-email')),
          LibValidators.required(TranslateService.localize('validations.required')),
        ],
      ],
      cpf: ['', [LibValidators.cpf(TranslateService.localize('validations.invalid'))]],
      externalId: ['', []],
      gender: [
        '',
        [
          LibValidators.required(TranslateService.localize('validations.required')),
          LibValidators.enumValue(EGenderDTO, TranslateService.localize('validations.not-allowed')),
        ],
      ],
      firstName: ['', [LibValidators.required(TranslateService.localize('validations.required'))]],
      lastName: ['', [LibValidators.required(TranslateService.localize('validations.required'))]],
      dateOfBirth: [
        '',
        [
          LibValidators.date(DEFAULT_DATE_FORMAT, TranslateService.localize('validations.invalid')),
          LibValidators.required(TranslateService.localize('validations.required')),
        ],
      ],
      phone: [
        '',
        [
          LibValidators.required(TranslateService.localize('validations.required')),
          LibValidators.cellphone(TranslateService.localize('validations.phone-invalid')),
        ],
      ],
      tag: [],
      cns: [
        '',
        [
          LibValidators.cns(TranslateService.localize('validations.invalid')),
          LibValidators.required(TranslateService.localize('validations.required')),
        ],
      ],
    };

    fieldValidation = PatientInviteUtils.applySkippedFields(fieldValidation, customSkippedFields);
    fieldValidation = PatientInviteUtils.applyRequiredFields(fieldValidation, customRequiredFields);

    return fieldValidation;
  }

  // Adjust field validation based on skipped fields
  static applySkippedFields = (fieldValidation: Record<string, any>, skippedFields?: string[]): Record<string, any> => {
    if (!skippedFields?.length) {
      return fieldValidation;
    }

    return Object.entries(fieldValidation).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: skippedFields.includes(key) ? ['', []] : value,
      }),
      {},
    );
  };

  // Apply required validators to specified fields
  static applyRequiredFields(fieldValidation: Record<string, any>, requiredFields?: string[]): Record<string, any> {
    if (!requiredFields?.length) {
      return fieldValidation;
    }

    return {
      ...fieldValidation,
      ...requiredFields.reduce((acc, field) => {
        if (!fieldValidation[field]) {
          return acc;
        }

        const [value, validators] = fieldValidation[field];
        return {
          ...acc,
          [field]: [value, [...validators, LibValidators.required(TranslateService.localize('validations.required'))]],
        };
      }, {}),
    };
  }

  static createPatientInviteDto(form: IPatientInviteFormModel): IPatientToInvite {
    return {
      cpf: form.cpf ? form.cpf.trim() : null,
      email: form.email ? form.email.trim() : null,
      external_id: form.externalId ? form.externalId.trim() : null,
      first_name: form.firstName.trim(),
      last_name: form.lastName.trim(),
      gender: form.gender,
      phone: form.phone ? form.phone.trim().replace(/[)(-]/g, '') : null,
      date_birth: form.dateOfBirth
        ? DateFormatter.stringToDate(form.dateOfBirth.trim().replace(/[/-]/g, '.'), {
            format: DEFAULT_DATE_FORMAT,
          })
        : null,
      tag: form.tag,
      cns: form.cns ? form.cns.trim() : null,
      is_atypical_child_guardian: form.isAtypicalChildGuardian ?? null,
    };
  }
}
